/**
 * DynamicFormRenderer
 * Renders a form from metadata (fields, sections) — handles validation,
 * conditional visibility, readonly, hidden, defaults.
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import FileFieldControl from "@/components/app/FileFieldControl";
import LookupFieldControl from "@/components/app/LookupFieldControl";
import NotesFieldControl from "@/components/app/NotesFieldControl";

function checkVisibility(rules, data) {
    if (!rules || rules.length === 0) return true;
    return rules.every((r) => {
        const v = data?.[r.field];
        switch (r.operator) {
            case "eq": return v === r.value;
            case "neq": return v !== r.value;
            case "in": return Array.isArray(r.value) && r.value.includes(v);
            case "not_in": return Array.isArray(r.value) && !r.value.includes(v);
            case "empty": return v == null || v === "" || (Array.isArray(v) && v.length === 0);
            case "not_empty": return !(v == null || v === "" || (Array.isArray(v) && v.length === 0));
            default: return true;
        }
    });
}

export function DynamicFormRenderer({
    sections = [], fields = [], data, onChange, lockedFields = new Set(),
    readOnly = false, layoutMode = "single", fieldControls = null, sectionControls = null,
    formId = null, recordId = null,
}) {
    const sortedSections = useMemo(() => [...sections].sort((a, b) => (a.order || 0) - (b.order || 0)), [sections]);
    const [activeTab, setActiveTab] = useState(null);
    const sectionMap = useMemo(() => {
        const m = new Map();
        sections.forEach((s) => m.set(s.id, s));
        return m;
    }, [sections]);

    const grouped = useMemo(() => {
        const buckets = new Map();
        fields.forEach((f) => {
            const key = f.section_id || "__no_section__";
            if (!buckets.has(key)) buckets.set(key, []);
            buckets.get(key).push(f);
        });
        buckets.forEach((arr) => arr.sort((a, b) => a.order - b.order));
        return buckets;
    }, [fields]);

    const set = (field, val) => {
        const next = { ...data, [field]: val };
        // If this field is a parent for any dependent dropdowns / lookups, reset their values
        // when the parent value actually changes so we never leave stale child values behind.
        if (data?.[field] !== val) {
            fields.forEach((dep) => {
                const isDependentDropdown = dep.field_type === "dropdown" && dep.dependent_parent_field === field;
                const isDependentLookup = dep.field_type === "lookup" && dep.lookup_filter_field === field;
                if ((isDependentDropdown || isDependentLookup) && next[dep.field_name] !== undefined) {
                    next[dep.field_name] = isDependentDropdown ? "" : "";
                }
            });
        }
        onChange?.(next);
    };

    const renderField = (f) => {
        const ctrl = fieldControls?.[f.field_name] || null; // "hide" | "readonly" | "mandatory"
        if (ctrl === "hide" || f.hidden) return null;
        if (!checkVisibility(f.visibility_rules, data)) return null;
        const isAutoGen = !!f.auto_generate?.enabled;
        const existingVal = data?.[f.field_name];
        const autoGenPreview = isAutoGen
            ? `${f.auto_generate.prefix || ""}${String(f.auto_generate.start ?? 1).padStart(f.auto_generate.padding ?? 0, "0")}${f.auto_generate.suffix || ""}`
            : null;
        const value = existingVal ?? f.default_value ?? (f.field_type === "multiselect" ? [] : "");
        const isLocked = readOnly || f.readonly || ctrl === "readonly" || lockedFields.has(f.field_name) || (isAutoGen && (existingVal == null || existingVal === ""));
        const isMandatory = (f.required || ctrl === "mandatory") && !isAutoGen;
        const id = `field-${f.field_name}`;
        const testid = `field-${f.field_name}`;

        const labelEl = (
            <div className="flex items-center gap-2 mb-1.5">
                <Label htmlFor={id} className="field-label">{f.field_label}</Label>
                {isMandatory && <span className="text-destructive text-xs" data-testid={`req-${f.field_name}`}>*</span>}
                {isLocked && !isAutoGen && <Badge variant="outline" className="rounded-sm font-mono text-[9px] py-0">Locked</Badge>}
                {isAutoGen && (
                    <Badge variant="outline" className="rounded-sm font-mono text-[9px] py-0 border-primary/40 text-primary" data-testid={`autogen-${f.field_name}`}>
                        auto · {autoGenPreview}
                    </Badge>
                )}
                {ctrl && (
                    <Badge variant="outline" className="rounded-sm font-mono text-[9px] py-0 border-primary/40 text-primary" data-testid={`wf-ctrl-${f.field_name}`}>
                        wf:{ctrl}
                    </Badge>
                )}
            </div>
        );

        let control = null;
        switch (f.field_type) {
            case "text":
            case "phone":
            case "email":
                control = <Input id={id} type={f.field_type === "email" ? "email" : "text"} value={value} disabled={isLocked} placeholder={f.placeholder || ""} onChange={(e) => set(f.field_name, e.target.value)} className="rounded-sm" data-testid={testid} />;
                break;
            case "number":
            case "decimal":
                control = <Input id={id} type="number" step={f.field_type === "decimal" ? "0.01" : "1"} value={value} disabled={isLocked} placeholder={f.placeholder || ""} onChange={(e) => set(f.field_name, e.target.value === "" ? "" : Number(e.target.value))} className="rounded-sm" data-testid={testid} />;
                break;
            case "textarea":
                control = <Textarea id={id} value={value} disabled={isLocked} placeholder={f.placeholder || ""} onChange={(e) => set(f.field_name, e.target.value)} className="rounded-sm" data-testid={testid} />;
                break;
            case "date":
                control = <Input id={id} type="date" value={value} disabled={isLocked} onChange={(e) => set(f.field_name, e.target.value)} className="rounded-sm" data-testid={testid} />;
                break;
            case "datetime":
                control = <Input id={id} type="datetime-local" value={value} disabled={isLocked} onChange={(e) => set(f.field_name, e.target.value)} className="rounded-sm" data-testid={testid} />;
                break;
            case "dropdown": {
                // Dependent dropdown — when `dependent_parent_field` is set, options come
                // from `dependent_options[parentValue]` instead of `options`. Locked until
                // parent is filled.
                const parentField = f.dependent_parent_field;
                const parentVal = parentField ? data?.[parentField] : null;
                let opts = f.options || [];
                let dependentLocked = false;
                if (parentField) {
                    if (parentVal == null || parentVal === "") {
                        opts = [];
                        dependentLocked = true;
                    } else {
                        const map = f.dependent_options || {};
                        opts = map[String(parentVal)] || map[parentVal] || [];
                    }
                }
                const dropDisabled = isLocked || dependentLocked;
                control = (
                    <Select value={value || ""} onValueChange={(v) => set(f.field_name, v)} disabled={dropDisabled}>
                        <SelectTrigger id={id} className="rounded-sm" data-testid={testid}>
                            <SelectValue placeholder={dependentLocked ? `Select ${parentField} first` : (f.placeholder || "Select")} />
                        </SelectTrigger>
                        <SelectContent>
                            {opts.length === 0 && !dependentLocked && <SelectItem value="__none__" disabled>No options</SelectItem>}
                            {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                );
                break;
            }
            case "multiselect":
                control = (
                    <div className="border border-border rounded-sm p-3 space-y-1 max-h-44 overflow-y-auto" data-testid={testid}>
                        {(f.options || []).map((o) => {
                            const arr = Array.isArray(value) ? value : [];
                            return (
                                <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Checkbox checked={arr.includes(o.value)} disabled={isLocked} onCheckedChange={(c) => set(f.field_name, c ? [...arr, o.value] : arr.filter((x) => x !== o.value))} />
                                    {o.label}
                                </label>
                            );
                        })}
                    </div>
                );
                break;
            case "checkbox":
                control = (
                    <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox checked={!!value} disabled={isLocked} onCheckedChange={(c) => set(f.field_name, !!c)} data-testid={testid} />
                        <span className="text-sm">{f.placeholder || "Yes"}</span>
                    </label>
                );
                break;
            case "radio":
                control = (
                    <RadioGroup value={value || ""} onValueChange={(v) => set(f.field_name, v)} disabled={isLocked} data-testid={testid}>
                        {(f.options || []).map((o) => (
                            <label key={o.value} className="flex items-center gap-2 text-sm">
                                <RadioGroupItem value={o.value} /> {o.label}
                            </label>
                        ))}
                    </RadioGroup>
                );
                break;
            case "toggle":
                control = (
                    <div className="flex items-center gap-2">
                        <Switch checked={!!value} disabled={isLocked} onCheckedChange={(c) => set(f.field_name, !!c)} data-testid={testid} />
                        <span className="text-sm text-muted-foreground">{value ? "On" : "Off"}</span>
                    </div>
                );
                break;
            case "file":
            case "image":
                control = (
                    <FileFieldControl
                        fieldName={f.field_name}
                        isImage={f.field_type === "image"}
                        value={value}
                        onChange={(v) => set(f.field_name, v)}
                        disabled={isLocked}
                        testid={testid}
                    />
                );
                break;
            case "notes":
                control = (
                    <NotesFieldControl
                        field={f}
                        value={value || []}
                        onChange={(v) => set(f.field_name, v)}
                        formId={formId}
                        recordId={recordId}
                        disabled={isLocked}
                        testid={testid}
                    />
                );
                break;
            case "lookup": {
                const parentField = f.lookup_filter_field;
                const parentVal = parentField ? data?.[parentField] : undefined;
                control = (
                    <LookupFieldControl
                        field={f}
                        value={value}
                        onChange={(v) => set(f.field_name, v)}
                        disabled={isLocked}
                        testid={testid}
                        parentValue={parentVal}
                    />
                );
                break;
            }
            case "table": {
                const cols = f.table_columns || [];
                const rows = Array.isArray(value) ? value : [];
                const updateRows = (next) => set(f.field_name, next);
                const newUid = () => `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
                const blankRow = () => ({ _uid: newUid(), ...Object.fromEntries(cols.map((c) => [c.name, ""])) });
                const sums = cols.map((c) => c.sum && c.type === "number"
                    ? rows.reduce((acc, r) => acc + (Number(r?.[c.name]) || 0), 0)
                    : null);
                control = (
                    <div className="border border-border rounded-sm bg-card overflow-x-auto" data-testid={testid}>
                        {cols.length === 0 ? (
                            <div className="p-3 text-xs text-muted-foreground text-center">No columns configured. Edit this field to add columns.</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border bg-secondary/40">
                                        {cols.map((c) => (
                                            <th key={c.name} className="px-2 py-1.5 text-left font-medium text-xs">
                                                {c.label}
                                                {c.required && <span className="text-destructive ml-0.5">*</span>}
                                                {c.sum && c.type === "number" && <span className="ml-1 font-mono text-[10px] text-primary">Σ</span>}
                                            </th>
                                        ))}
                                        {!isLocked && <th className="px-2 py-1.5 w-8" />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 && (
                                        <tr><td colSpan={cols.length + 1} className="px-3 py-3 text-center text-xs text-muted-foreground">No rows yet.</td></tr>
                                    )}
                                    {rows.map((row, ri) => {
                                        const rowKey = row?._uid || `row-${ri}`;
                                        return (
                                        <tr key={rowKey} className="border-b border-border last:border-b-0" data-testid={`${testid}-row-${ri}`}>
                                            {cols.map((c) => (
                                                <td key={c.name} className="px-1 py-0.5">
                                                    {c.type === "textarea" ? (
                                                        <Textarea
                                                            value={row?.[c.name] ?? ""}
                                                            disabled={isLocked}
                                                            onChange={(e) => {
                                                                const next = [...rows];
                                                                next[ri] = { ...next[ri], [c.name]: e.target.value };
                                                                updateRows(next);
                                                            }}
                                                            rows={2}
                                                            className="rounded-sm border-transparent hover:border-border focus:border-primary text-sm min-h-[36px] py-1"
                                                            data-testid={`${testid}-row-${ri}-${c.name}`}
                                                        />
                                                    ) : c.type === "lookup" ? (
                                                        <LookupFieldControl
                                                            field={{
                                                                lookup_form_id: c.lookup_form_id,
                                                                lookup_display_field: c.lookup_display_field,
                                                                lookup_value_field: c.lookup_value_field,
                                                                field_label: c.label,
                                                            }}
                                                            value={row?.[c.name] ?? ""}
                                                            onChange={(v) => {
                                                                const next = [...rows];
                                                                next[ri] = { ...next[ri], [c.name]: v };
                                                                updateRows(next);
                                                            }}
                                                            disabled={isLocked}
                                                            testid={`${testid}-row-${ri}-${c.name}`}
                                                        />
                                                    ) : c.type === "dropdown" ? (
                                                        <Select
                                                            value={String(row?.[c.name] ?? "")}
                                                            disabled={isLocked}
                                                            onValueChange={(v) => {
                                                                const next = [...rows];
                                                                next[ri] = { ...next[ri], [c.name]: v };
                                                                updateRows(next);
                                                            }}
                                                        >
                                                            <SelectTrigger className="rounded-sm h-8 border-transparent hover:border-border" data-testid={`${testid}-row-${ri}-${c.name}`}>
                                                                <SelectValue placeholder="—" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {(c.options || []).map((o) => (
                                                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    ) : (
                                                        <Input
                                                            type={c.type === "number" ? "number" : c.type === "date" ? "date" : "text"}
                                                            value={row?.[c.name] ?? ""}
                                                            disabled={isLocked}
                                                            onChange={(e) => {
                                                                const next = [...rows];
                                                                const v = c.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value;
                                                                next[ri] = { ...next[ri], [c.name]: v };
                                                                updateRows(next);
                                                            }}
                                                            className="rounded-sm h-8 border-transparent hover:border-border focus:border-primary"
                                                            data-testid={`${testid}-row-${ri}-${c.name}`}
                                                        />
                                                    )}
                                                </td>
                                            ))}
                                            {!isLocked && (
                                                <td className="px-1 py-0.5 text-center">
                                                    <button type="button" onClick={() => updateRows(rows.filter((_, j) => j !== ri))} className="text-muted-foreground hover:text-destructive p-1" data-testid={`${testid}-row-${ri}-del`}>
                                                        ×
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                        );
                                    })}
                                </tbody>
                                {sums.some((s) => s !== null) && rows.length > 0 && (
                                    <tfoot>
                                        <tr className="bg-primary/5 border-t-2 border-primary/30 font-mono text-xs">
                                            {cols.map((c, ci) => (
                                                <td key={c.name} className="px-2 py-1.5">
                                                    {sums[ci] !== null
                                                        ? <span className="font-semibold text-primary" data-testid={`${testid}-sum-${c.name}`}>Σ {Number(sums[ci]).toLocaleString()}</span>
                                                        : null}
                                                </td>
                                            ))}
                                            {!isLocked && <td />}
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        )}
                        {!isLocked && cols.length > 0 && (
                            <div className="border-t border-border bg-secondary/30 px-2 py-1.5 flex justify-end">
                                <button type="button" onClick={() => updateRows([...rows, blankRow()])} className="text-xs text-primary hover:underline font-medium" data-testid={`${testid}-add-row`}>
                                    + Add row
                                </button>
                            </div>
                        )}
                    </div>
                );
                break;
            }
            default:
                control = <Input id={id} value={value} disabled={isLocked} onChange={(e) => set(f.field_name, e.target.value)} className="rounded-sm" data-testid={testid} />;
        }
        const widthClass = f.field_type === "table" ? "md:col-span-2 xl:col-span-3" : "";
        return (
            <div key={f.id} className={`${widthClass} ${f.desktop_only ? "hidden md:block" : f.mobile_only ? "md:hidden" : ""}`.trim()}>
                {labelEl}
                {control}
                {f.help_text && <p className="text-xs text-muted-foreground mt-1">{f.help_text}</p>}
            </div>
        );
    };

    const orderedSections = useMemo(() => {
        // Filter out sections hidden by workflow section_controls.
        return sortedSections.filter((s) => sectionControls?.[s.id] !== "hide");
    }, [sortedSections, sectionControls]);
    // Hide sectionless fields when the synthetic "__all__" (or "__no_section__") is marked hidden.
    const noSectionFields = (sectionControls?.__all__ === "hide" || sectionControls?.__no_section__ === "hide")
        ? []
        : (grouped.get("__no_section__") || []);

    // ────────────────────────────────────────────────────────────────────────
    // LAYOUT 3 — Tabbed (horizontal pill tabs across the top, full-width body)
    // ────────────────────────────────────────────────────────────────────────
    if (layoutMode === "tabs" && orderedSections.length > 0) {
        const active = orderedSections.find((s) => s.id === activeTab) ? activeTab : orderedSections[0].id;
        const activeSection = orderedSections.find((s) => s.id === active);
        const activeItems = grouped.get(active) || [];
        return (
            <div className="space-y-6" data-testid="form-layout-tabs">
                {/* "No section" fields show as a header strip — always visible across tabs */}
                {noSectionFields.length > 0 && (
                    <div className="brut-section p-5">
                        <div className="grid md:grid-cols-3 gap-x-6 gap-y-4">{noSectionFields.map(renderField)}</div>
                    </div>
                )}

                {/* Horizontal pill tabs */}
                <div className="border-b border-border" data-testid="form-section-tabs">
                    <div className="flex items-center gap-1 overflow-x-auto px-1 -mb-px">
                        {orderedSections.map((s) => {
                            const items = grouped.get(s.id) || [];
                            const isActive = s.id === active;
                            return (
                                <button
                                    key={s.id} type="button"
                                    onClick={() => setActiveTab(s.id)}
                                    className={`relative inline-flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap transition-colors rounded-t-sm ${
                                        isActive
                                            ? "text-primary font-semibold bg-primary/5"
                                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                                    }`}
                                    data-testid={`tab-${s.id}`}
                                >
                                    <span>{s.title}</span>
                                    <span className={`text-xs font-mono tabular-nums rounded-sm px-1.5 py-0.5 ${isActive ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
                                        {items.length}
                                    </span>
                                    {isActive && <span className="absolute inset-x-3 -bottom-px h-0.5 bg-primary rounded-t-sm" />}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Full-width body */}
                <div className="brut-section p-6 bg-card" data-testid={`tab-panel-${active}`}>
                    {activeSection?.description && (
                        <p className="text-sm text-muted-foreground mb-6">{activeSection.description}</p>
                    )}
                    {activeItems.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-6 text-center">No fields in this section yet.</div>
                    ) : (
                        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-5">
                            {activeItems.map(renderField)}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    // LAYOUT 2 — Stepper (Google Forms style: one section per step + nav)
    // ────────────────────────────────────────────────────────────────────────
    if (layoutMode === "stepper" && orderedSections.length > 0) {
        const stepIndex = Math.min(
            Math.max(0, orderedSections.findIndex((s) => s.id === activeTab)),
            orderedSections.length - 1,
        );
        const idx = stepIndex < 0 ? 0 : stepIndex;
        const current = orderedSections[idx];
        const items = grouped.get(current.id) || [];
        const goPrev = () => setActiveTab(orderedSections[Math.max(0, idx - 1)].id);
        const goNext = () => setActiveTab(orderedSections[Math.min(orderedSections.length - 1, idx + 1)].id);
        const progress = ((idx + 1) / orderedSections.length) * 100;
        return (
            <div className="space-y-6 max-w-3xl mx-auto" data-testid="form-layout-stepper">
                {noSectionFields.length > 0 && (
                    <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                        {noSectionFields.map(renderField)}
                    </div>
                )}
                <div data-testid="stepper-progress">
                    <div className="flex items-center justify-between text-xs font-mono mb-2">
                        <span className="text-muted-foreground">Step {idx + 1} of {orderedSections.length}</span>
                        <span className="text-muted-foreground">{Math.round(progress)}%</span>
                    </div>
                    <div className="h-1 bg-secondary rounded-sm overflow-hidden">
                        <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    {/* Dots / chips */}
                    <div className="flex items-center gap-2 mt-4 flex-wrap">
                        {orderedSections.map((s, i) => {
                            const isActive = i === idx;
                            const isDone = i < idx;
                            return (
                                <button
                                    key={s.id} type="button" onClick={() => setActiveTab(s.id)}
                                    data-testid={`step-chip-${s.id}`}
                                    className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-sm transition-colors ${
                                        isActive
                                            ? "bg-primary text-primary-foreground"
                                            : isDone
                                            ? "bg-primary/10 text-primary"
                                            : "bg-secondary text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    <span className="font-mono text-[10px] tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="hidden sm:inline">{s.title}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="brut-section p-8 bg-card" data-testid={`stepper-panel-${current.id}`}>
                    <h3 className="text-2xl font-bold tracking-tight">{current.title}</h3>
                    {current.description && <p className="text-sm text-muted-foreground mt-1 mb-6">{current.description}</p>}
                    {items.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-6 text-center">No fields in this step.</div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5 mt-6">{items.map(renderField)}</div>
                    )}
                </div>
                {!readOnly && (
                    <div className="flex items-center justify-between pt-2" data-testid="stepper-nav">
                        <button
                            type="button" onClick={goPrev} disabled={idx === 0}
                            className="text-sm font-medium px-4 py-2 rounded-sm border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="stepper-prev-btn"
                        >← Previous</button>
                        {idx < orderedSections.length - 1 ? (
                            <button
                                type="button" onClick={goNext}
                                className="text-sm font-medium px-5 py-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
                                data-testid="stepper-next-btn"
                            >Next →</button>
                        ) : (
                            <span className="text-xs text-muted-foreground font-mono">Last step · submit below</span>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    // LAYOUT 1 — Single form (all sections stacked vertically) — also fallback
    // ────────────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6" data-testid="form-layout-single">
            {noSectionFields.length > 0 && (
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                    {noSectionFields.map(renderField)}
                </div>
            )}
            {orderedSections.map((s) => {
                const items = grouped.get(s.id) || [];
                if (items.length === 0) return null;
                return (
                    <div key={s.id} className="brut-section p-6">
                        <div className="border-b border-border pb-3 mb-4">
                            <h3 className="text-base font-semibold tracking-tight">{s.title}</h3>
                            {s.description && <p className="text-sm text-muted-foreground mt-1">{s.description}</p>}
                        </div>
                        <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">{items.map(renderField)}</div>
                    </div>
                );
            })}
        </div>
    );
}
